/**
 * CWR 2.4 — service folded-in date/time picker (mockup .bk block): a date strip
 * (available days enabled + dot, unavailable struck/disabled) then time pills
 * grouped Morning/Afternoon/Evening. Selection is keyboard-operable.
 *
 * 2.4.1: the strip is grouped into month sections with a sticky month spine (B);
 * each period's pill row scrolls horizontally on overflow (D, CSS); while a new
 * date's slots load the previous pills stay mounted, dimmed + tap-blocked (C).
 */
import type { HubServiceSlot } from '@/api/types';
import { DateStrip, type DateStripMonth } from '@/components/DateStrip/DateStrip';
import { formatSlotTime, type DateChip, type PeriodGroup, type SlotPeriod } from '../detailFormat';
import styles from '../SessionDetailPage.module.css';

// All three day-parts are ALWAYS rendered in this order, even when a period has no
// availability (it shows a faint "None available"). Holding the slot area at a stable
// three-row height stops the page from collapsing/jumping when you switch between a
// busy day and a sparse one (CWR 2.4.1 — keeps your scroll place across dates).
const PERIOD_ORDER: Array<{ period: SlotPeriod; label: string }> = [
  { period: 'morning', label: 'Morning' },
  { period: 'afternoon', label: 'Afternoon' },
  { period: 'evening', label: 'Evening' },
];

export interface DateTimePickerProps {
  dates: DateChip[];
  selectedDate: string | null;
  onSelectDate: (ymd: string) => void;
  tzLabel: string;
  tz: string;
  /** No month-date query has returned yet — show a loading affordance, not an
   *  all-disabled strip that masquerades as "no availability". */
  datesLoading?: boolean;
  /** Every month-date query failed with nothing available — show an error, not
   *  a disabled strip. */
  datesError?: boolean;
  slotsLoading: boolean;
  /** New date's slots are loading while the previous date's slots are still shown. */
  slotsUpdating: boolean;
  periodGroups: PeriodGroup[];
  selectedSlot: string | null;
  onSelectSlot: (slot: HubServiceSlot) => void;
}

/**
 * Fold the contiguous, ordered date chips into the shared strip's month
 * sections (B). An unavailable day is disabled AND struck; an available one
 * carries the dot.
 */
function groupByMonth(dates: DateChip[], selectedDate: string | null): DateStripMonth[] {
  const groups: DateStripMonth[] = [];
  for (const c of dates) {
    const day = {
      date: c.ymd,
      weekday: c.dow,
      dayOfMonth: c.dnum,
      selected: c.ymd === selectedDate,
      disabled: !c.available,
      marked: c.available,
      ariaLabel: `${c.dow} ${c.monthLabel} ${c.dnum}`,
    };
    const last = groups[groups.length - 1];
    if (!last || last.key !== c.monthKey) groups.push({ key: c.monthKey, label: c.monthLabel, days: [day] });
    else last.days.push(day);
  }
  return groups;
}

export function DateTimePicker({
  dates,
  selectedDate,
  onSelectDate,
  tzLabel,
  tz,
  datesLoading = false,
  datesError = false,
  slotsLoading,
  slotsUpdating,
  periodGroups,
  selectedSlot,
  onSelectSlot,
}: DateTimePickerProps) {
  const monthGroups = groupByMonth(dates, selectedDate);
  // Settled with nothing bookable. buildDateStrip still emits a full month of chips
  // when the availability union is empty, so "no availability" is "no chip is
  // available", NOT an empty array — an all-struck strip would otherwise say nothing.
  const datesEmpty = !datesLoading && !datesError && !dates.some((c) => c.available);
  return (
    <div className={styles.bk} data-testid="date-time-picker">
      <div className={styles.bkHead}>
        <h3>Select a date &amp; time</h3>
        {tzLabel && <span className={styles.bkTz}>{tzLabel}</span>}
      </div>

      {datesLoading ? (
        <div className={styles.pickerState}>Loading availability…</div>
      ) : datesError ? (
        <div className={styles.pickerState}>Couldn&rsquo;t load availability. Please try again.</div>
      ) : datesEmpty ? (
        <div className={styles.pickerState}>
          Fully booked right now. New times open up as the schedule moves forward.
        </div>
      ) : (
        <DateStrip
          months={monthGroups}
          onSelect={onSelectDate}
          ariaLabel="Select a date"
          className={styles.dateStripTheme}
        />
      )}

      {/* No bookable day ⇒ no day-part rows either: three "None available" headings
          under a fully-booked message is noise, and a stale selectedDate can survive
          into this state when the horizon empties out under an open page. */}
      {!datesEmpty &&
        selectedDate &&
        (slotsLoading ? (
          <div className={styles.pickerState}>Loading times…</div>
        ) : (
          PERIOD_ORDER.map(({ period, label }) => {
            const slots = periodGroups.find((g) => g.period === period)?.slots ?? [];
            return (
              <div key={period} className={styles.timeGroup}>
                <span className={styles.glabel}>{label}</span>
                {slots.length > 0 ? (
                  <div
                    // Key on the date so the scroll container REMOUNTS when the date
                    // changes — otherwise its horizontal scrollLeft persists and the
                    // new day's times start mid-scroll (CWR 2.4.1-C).
                    key={`${period}-${selectedDate}`}
                    className={`${styles.timePills} ${slotsUpdating ? styles.updating : ''}`}
                    data-testid="time-pills"
                  >
                    {slots.map((s) => (
                      <button
                        key={s.startsAt}
                        type="button"
                        data-testid="time-pill"
                        aria-pressed={s.startsAt === selectedSlot}
                        disabled={slotsUpdating}
                        className={`${styles.timePill} ${s.startsAt === selectedSlot ? styles.selected : ''}`}
                        onClick={() => onSelectSlot(s)}
                      >
                        {formatSlotTime(s.startsAt, tz)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={styles.timeEmpty}>None available</div>
                )}
              </div>
            );
          })
        ))}
    </div>
  );
}
